import { useCallback, useEffect, useState } from "react";

// Same worker URL the push layer registers (services/pushNotifications.js).
// register() with an identical URL+scope returns the existing
// registration, so the two call sites can never double-install.
const SW_URL = "/sw.js";

/**
 * useAppUpdate — registers the PWA service worker and surfaces the
 * prompt-based update flow (vite.config.js registerType: 'prompt').
 *
 *   updateAvailable — a new deploy's worker is installed and WAITING
 *   applyUpdate()   — posts SKIP_WAITING to the waiting worker, then
 *                     reloads once it takes control, landing the user
 *                     on the fresh precached bundle
 *
 * Registration is skipped entirely when:
 *   • window.Capacitor exists — the iOS native build updates through
 *     TestFlight / the App Store; a SW there would fight the shell
 *   • the browser has no serviceWorker support
 *   • dev mode — vite only emits sw.js in production builds
 *
 * Two triggers force a real update check (both call registration.update()):
 *   1. On mount, right after register() resolves — covers cold launches
 *      (a force-quit + relaunch, or the first open of the day). Without
 *      this, a cold launch had NO explicit check at all: it only
 *      adopted whatever the browser happened to have already found on
 *      its own, leaving cold launches fully dependent on iOS Safari's
 *      less-reliable implicit "navigate to an in-scope page" check.
 *   2. A visibilitychange listener, every time the PWA returns to the
 *      foreground from an already-running (backgrounded, not killed)
 *      session — the standalone iOS shell almost never re-navigates,
 *      so without this a backgrounded install could miss deploys for
 *      weeks. This can't fire on a cold launch (no prior hidden state
 *      to transition from), which is exactly why trigger 1 exists.
 */
export function useAppUpdate() {
  const [waitingWorker, setWaitingWorker] = useState(null);

  useEffect(() => {
    if (window.Capacitor) return undefined;
    if (!("serviceWorker" in navigator)) return undefined;
    if (import.meta.env && import.meta.env.DEV) return undefined;

    let disposed = false;
    let registration = null;

    const adoptWaiting = (reg) => {
      if (!disposed && reg && reg.waiting) setWaitingWorker(reg.waiting);
    };

    navigator.serviceWorker
      .register(SW_URL, { scope: "/" })
      .then((reg) => {
        if (disposed || !reg) return;
        registration = reg;

        // A worker may already be waiting from a deploy that happened
        // while the app was closed.
        adoptWaiting(reg);

        // One may arrive later: installing → installed(+controller)
        // means "new version ready, waiting for the prompt".
        reg.addEventListener("updatefound", () => {
          const incoming = reg.installing;
          if (!incoming) return;
          incoming.addEventListener("statechange", () => {
            if (incoming.state === "installed" && navigator.serviceWorker.controller) {
              adoptWaiting(reg);
            }
          });
        });

        // Force a real check on every launch, cold or warm — see the
        // trigger-1 note above. One check per mount; no polling/interval.
        reg.update().catch(() => {});
      })
      .catch(() => {
        // Registration failure (private mode, storage pressure) just
        // means no update prompts — the app itself is unaffected.
      });

    // Foreground re-check: ask the browser to look for a newer sw.js
    // whenever the app becomes visible again.
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      const check = registration
        ? Promise.resolve([registration])
        : navigator.serviceWorker.getRegistrations?.() || Promise.resolve([]);
      check.then((regs) => regs.forEach((r) => r.update().catch(() => {})));
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  const applyUpdate = useCallback(() => {
    if (!waitingWorker) return;
    // Reload only after the new worker has taken control, so the fetch
    // for the reloaded page is served by the NEW precache, not the old.
    navigator.serviceWorker.addEventListener(
      "controllerchange",
      () => window.location.reload(),
      { once: true },
    );
    waitingWorker.postMessage({ type: "SKIP_WAITING" });
  }, [waitingWorker]);

  return { updateAvailable: Boolean(waitingWorker), applyUpdate };
}
